import { useState } from 'react';
import { FileText } from 'lucide-react';
import { LookbookQR } from '@/components/activation/lookbook/LookbookPrimitives';
import { LookbookPageFrame, LOOKBOOK_PAGE_W, LOOKBOOK_PAGE_H } from '@/components/activation/lookbook/LookbookPageFrame';
import { LOOKBOOK_DESIGNS } from '@/components/activation/lookbook/lookbookDesigns';
import { PosterPageFrame, POSTER_PAGE_W, POSTER_PAGE_H } from '@/components/activation/poster/PosterPageFrame';
import { POSTER_DESIGNS } from '@/components/activation/poster/posterDesigns';
import { resolveLookbookBrand } from '@/components/activation/lookbook/lookbookBrand';
import { LookbookBrand } from '@/types/activation/lookbook';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import { PrintOrderItem } from '@/types/activation/print-order';

const IMAGE_URL_PATTERN = /\.(png|jpe?g|webp|gif|svg)(\?|$)/i;

/** A live-rendered, "contain"-fit miniature of a full-size design, centered in a fixed box. */
const ScaledBox = ({
  naturalW,
  naturalH,
  boxSize,
  children,
}: {
  naturalW: number;
  naturalH: number;
  boxSize: number;
  children: React.ReactNode;
}) => {
  const scale = boxSize / Math.max(naturalW, naturalH);
  return (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: naturalW,
        height: naturalH,
        transform: `translate(-50%, -50%) scale(${scale})`,
      }}
    >
      {children}
    </div>
  );
};

const Frame = ({ boxSize, children }: { boxSize: number; children: React.ReactNode }) => (
  <div
    className="relative shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
    style={{ width: boxSize, height: boxSize }}
  >
    {children}
  </div>
);

const lookbookDesignFor = (item: PrintOrderItem) => {
  const designId = item.id.startsWith('design:lookbook:') ? item.id.slice('design:lookbook:'.length) : null;
  const index = designId ? LOOKBOOK_DESIGNS.findIndex((d) => d.id === designId) : -1;
  return { design: index >= 0 ? LOOKBOOK_DESIGNS[index] : LOOKBOOK_DESIGNS[0], index: index >= 0 ? index : 0 };
};

const posterDesignFor = (item: PrintOrderItem) => {
  const designId = item.id.startsWith('design:poster:') ? item.id.slice('design:poster:'.length) : null;
  return designId ? POSTER_DESIGNS.find((d) => d.id === designId) || POSTER_DESIGNS[0] : POSTER_DESIGNS[0];
};

interface PrintOrderItemThumbProps {
  item: PrintOrderItem;
  pack: ActivationPackWithTarget;
  boxSize?: number;
}

/**
 * Small live preview of a print order item. Most items map back to one of our
 * design components (lookbook page, poster page, QR sticker, app poster), so
 * rather than rasterizing/caching a bitmap we just render the real thing at a
 * tiny scale — same trick the lookbook/poster galleries already use.
 */
export const PrintOrderItemThumb = ({ item, pack, boxSize = 52 }: PrintOrderItemThumbProps) => {
  const [imageFailed, setImageFailed] = useState(false);
  const brand: LookbookBrand = resolveLookbookBrand(pack);

  if (item.assetType === 'lookbook_pdf') {
    const { design, index } = lookbookDesignFor(item);
    return (
      <Frame boxSize={boxSize}>
        <ScaledBox naturalW={LOOKBOOK_PAGE_W} naturalH={LOOKBOOK_PAGE_H} boxSize={boxSize}>
          <LookbookPageFrame brand={brand} design={design} index={index + 1} total={LOOKBOOK_DESIGNS.length} />
        </ScaledBox>
      </Frame>
    );
  }

  if (item.assetType === 'poster_pack_pdf') {
    const design = posterDesignFor(item);
    return (
      <Frame boxSize={boxSize}>
        <ScaledBox naturalW={POSTER_PAGE_W} naturalH={POSTER_PAGE_H} boxSize={boxSize}>
          <PosterPageFrame brand={brand} design={design} />
        </ScaledBox>
      </Frame>
    );
  }

  if (item.assetType === 'sticker_pdf') {
    return (
      <Frame boxSize={boxSize}>
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: '#F4F7FB' }}>
          <ScaledBox naturalW={160} naturalH={160} boxSize={boxSize - 8}>
            <LookbookQR brand={brand} size={160} />
          </ScaledBox>
        </div>
      </Frame>
    );
  }

  if (item.assetType === 'app_poster_pdf' || item.assetType === 'app_poster_png') {
    return (
      <Frame boxSize={boxSize}>
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ background: `linear-gradient(150deg, ${brand.primary}, ${brand.secondary})` }}
        >
          <ScaledBox naturalW={160} naturalH={160} boxSize={boxSize - 16}>
            <LookbookQR brand={brand} size={160} />
          </ScaledBox>
        </div>
      </Frame>
    );
  }

  if (!imageFailed && IMAGE_URL_PATTERN.test(item.url)) {
    return (
      <Frame boxSize={boxSize}>
        <img
          src={item.url}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      </Frame>
    );
  }

  return (
    <Frame boxSize={boxSize}>
      <div className="flex h-full w-full items-center justify-center bg-gray-50">
        <FileText className="h-5 w-5 text-gray-400" />
      </div>
    </Frame>
  );
};
